import React, { useEffect, useRef, useState } from 'react'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import { getVariantItem } from '@helpers/filters'
import { encode } from '@helpers/shopify'
import { useRouter } from 'next/router'
import { useDispatch } from 'react-redux'
import { openCart } from '@store/redux/actions/otherActions'
import addToCart from '@store/redux/actions/addToCart'
import { GIFT_CARD } from '@consts/index'
import FitOption from '@components/ui/FitOption'
import FitOptionWrapper from '@components/ui/FitOption/FitOptionWrapper'
import ProductGallery from '@components/product/ProductGallery'
import Translations from '@helpers/translations'
import { prefixLocale } from '@helpers/env'
import {
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel
} from '@components/ui/Accordion'
import { ProductsBreakdownProps } from '@components/ui/ProductsBreakdown'
import productWrapperStyles from '../styles.module.scss'
import { PHYSICAL_GIFT_CARD_IMAGES } from './physicalGiftCardImages'
import styles from './styles.module.scss'
import {
  ProductListProduct,
  ProductsList
} from '@components/product/ProductList'
import { SubmitButtonHandlers } from '@components/ui/Button/SubmitButton'
import { Choice } from '@components/product/GiftCard/GiftCardMessagingOptions'
import { ProductWithVariants } from 'types/product'
import { CartLinesInput } from 'types/checkout'
import { getProductsWithUpdatedVariants } from '@helpers/filters/updateVariants'
import { getProductInventoryByProductType } from '@api/inventory'
import trackError from '@helpers/trackError'

const GiftCardFormContainer = dynamic(() => import('./GiftCardFormContainer'))
const GiftCardMessagingOptions = dynamic(
  () => import('./GiftCardMessagingOptions')
)
const GiftCardAmountOptions = dynamic(() => import('./GiftCardAmountOptions'))
const ProductHeader = dynamic(() => import('@components/product/ProductHeader'))
const ProductsBreakdown = dynamic(
  () => import('@components/ui/ProductsBreakdown')
)

const getGiftCardGalleryImages = (
  product: ProductWithVariants,
  selectedGiftCardOption: Choice
) => {
  const getStaticGiftCardImages =
    PHYSICAL_GIFT_CARD_IMAGES[selectedGiftCardOption]
  return !!getStaticGiftCardImages && !/digital/.test(product.handle)
    ? getStaticGiftCardImages.slice() // TS - converts to mutable array
    : product.images
}

function getProductTags(tags: string) {
  return !!tags ? tags.replace(/\s/g, '').split(',').join('') : ''
}

function getIsDigitalGiftCard(tags: string) {
  return !!tags && tags.includes('gift-card-digital')
}

function getSelectedVariantAmount(selectedProduct?: ProductListProduct) {
  if (selectedProduct?.variants) {
    return (
      selectedProduct.variants[0]?.options.find(
        variant => variant.type === 'amount'
      )?.value || ''
    )
  }

  return GIFT_CARD.values[1]
}

type GiftCardProps = {
  productsList: ProductsList
  productsBreakdown: ProductsBreakdownProps | null
}

function GiftCard({ productsList, productsBreakdown }: GiftCardProps) {
  const router = useRouter()
  const { query } = router
  const { productHandle } = query
  const dispatch = useDispatch()

  const [variantExist, setVariantExist] = useState(false)
  const [products, setProducts] = useState(productsList)
  const [imageIndex, setImageIndex] = useState(0)
  const [choice, setChoice] = useState<Choice>('friend-dont-let-friends')
  const giftCardProducts = products.flatMap(product => product.products)

  const selectedProduct = giftCardProducts.find(product => {
    if (!productHandle) {
      return product
    }
    return product.handle.toLowerCase() === productHandle
  })

  const [selectedAmount, setSelectedAmount] = useState(
    getSelectedVariantAmount(selectedProduct)
  )

  const shouldShowProductsBreakdown =
    productsBreakdown?.categories &&
    Number(productsBreakdown?.categories.items.length) > 0

  const productTags = !!selectedProduct
    ? getProductTags(selectedProduct.tags)
    : ''
  const isDigitalGiftCard = getIsDigitalGiftCard(productTags)
  const option = [
    {
      position: 1,
      type: 'amount',
      value: selectedAmount
    }
  ]

  const selectedVariant = variantExist
    ? getVariantItem(selectedProduct, option) || []
    : []

  const hasFetchedVariants = useRef(false)
  useEffect(() => {
    // Fetch gift card inventory once on mount.
    if (hasFetchedVariants.current) return
    hasFetchedVariants.current = true

    const fetchVariants = async () => {
      try {
        const productType = selectedProduct?.product_type
        const variants = await getProductInventoryByProductType(
          productType as string
        )

        const productsWithVariants = products.map(productType => ({
          ...productType,
          products: getProductsWithUpdatedVariants(
            productType.products,
            variants.productInventoryByType.items
          )
        }))
        setProducts(productsWithVariants)
        setVariantExist(true)
      } catch (error) {
        trackError(new Error('Failed to fetch Gift Card inventory'), {
          error: error as Error,
          section: 'GiftCard'
        })
      }
    }
    fetchVariants()
  }, [products, selectedProduct?.product_type])

  const triggerOpenCart = () => dispatch(openCart())

  const addItemToCart = async ({
    triggerButtonSuccess,
    triggerButtonError
  }: SubmitButtonHandlers) => {
    const { id } = selectedVariant[0]
    const customAttributes = !isDigitalGiftCard
      ? [
          {
            key: 'message',
            value: GIFT_CARD.radios[imageIndex].value
          }
        ]
      : []

    try {
      const cartItemProps: CartLinesInput = {
        quantity: 1,
        merchandiseId: encode('ProductVariant', id),
        attributes: customAttributes
      }
      await dispatch(addToCart([cartItemProps], 'Product Page'))

      triggerButtonSuccess()
      triggerOpenCart()
    } catch (error) {
      trackError(new Error('Failed to add gift card to cart'), {
        error: error as Error,
        section: 'GiftCard'
      })
      triggerButtonError()
    }
  }

  const selectOption = (value: string) => setSelectedAmount(value)

  const onChangeOption = (choice: Choice, i: number) => {
    setChoice(choice)
    setImageIndex(i)
  }

  const handleSelect = (handle: string) => {
    router.replace(
      { pathname: router.route, query: { productHandle: handle } },
      `${prefixLocale()}${router.route.replace('[productHandle]', handle)}`,
      { shallow: true }
    )
  }

  if (!selectedProduct) return null
  const productHeaderAttributes = {
    product: {
      ...selectedProduct,
      product_type: 'Gift Cards',
      price: ''
    },
    byline: Translations.localise('product.gift-card.byline')
  }

  return (
    <div className={productWrapperStyles.productContainer}>
      <Head>
        <title>Gift Card - SPOKE</title>
        <meta
          name="description"
          content={Translations.localise('product.gift-card.title')}
          key="description"
        />
        <meta
          name="og:description"
          property="og:description"
          content={Translations.localise('product.gift-card.title')}
          key="og:description"
        />
      </Head>
      <div className={productWrapperStyles.productWrapper}>
        <div className={productWrapperStyles.productImages}>
          <ProductHeader
            {...productHeaderAttributes}
            classes={productWrapperStyles.sidebar__productHeader}
          />
          <ProductGallery
            title={selectedProduct.title}
            images={getGiftCardGalleryImages(selectedProduct, choice)}
          />
        </div>
        <div className={productWrapperStyles.productForm__content}>
          <ProductHeader
            {...productHeaderAttributes}
            classes={productWrapperStyles.sidebar__productHeader}
          />

          <GiftCardFormContainer
            label={Translations.localise('product.gift-card.voucher-type')}
          >
            <FitOptionWrapper>
              {giftCardProducts.map(({ title, handle, tags }) => (
                <FitOption
                  classes="text no-top-margin"
                  optionValue={
                    getIsDigitalGiftCard(tags)
                      ? 'Digital'
                      : Translations.localise('product.gift-card.physical')
                  }
                  key={title}
                  selected={title === selectedProduct.title}
                  onSelect={() => handleSelect(handle)}
                  active
                />
              ))}
              {isDigitalGiftCard && (
                <div className={styles.giftCard__helper}>
                  {Translations.localise('product.gift-card.helper')}
                </div>
              )}
            </FitOptionWrapper>
          </GiftCardFormContainer>

          {!isDigitalGiftCard && (
            <GiftCardFormContainer label="Gift card design">
              <GiftCardMessagingOptions
                handleOnChangeOption={onChangeOption}
                choice={choice}
              />
            </GiftCardFormContainer>
          )}

          <GiftCardFormContainer
            label={Translations.localise('product.gift-card.amount')}
          >
            <GiftCardAmountOptions
              product={selectedProduct}
              handleOnSelect={selectOption}
              selectedAmount={selectedAmount}
              handleAddItemToCart={addItemToCart}
              name={selectedProduct.handle}
            />
          </GiftCardFormContainer>

          <Accordion
            id="ProductInfoAccordion"
            classes={styles.accordion}
            defaultOpen={[0]}
          >
            <AccordionItem>
              <AccordionButton>
                {Translations.localise('product.accordion.product-details')}
              </AccordionButton>
              <AccordionPanel>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedProduct.body_html
                  }}
                />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </div>
      </div>
      {shouldShowProductsBreakdown && (
        <ProductsBreakdown
          {...productsBreakdown}
          byline={`${Translations.localise(
            'product.not-for-you-1'
          )} Gift Cards ${Translations.localise('product.not-for-you-2')}`}
          productType={selectedProduct?.product_type}
          category="Gift Cards"
        />
      )}
    </div>
  )
}

export default GiftCard
